import React, { useEffect, useMemo, useRef, useState } from 'react';
import Button from '@common/ui/button/Button';
import DwFormField from '@common/ui/formfield/DwFormField';
import ModalDialog from '@common/ui/modal/ModalDialog';
import api from '@common/utils/api';
import formutils from '@common/utils/formutils';
import ErrorBox from '@components/common/ErrorBox';
import classNames from 'classnames';
import createDecorator from 'final-form-focus';
import { Form } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import Tooltip from '@common/ui/tooltip/Tooltip';
import analytics from '@utils/analytics';
import { getTimezoneOffset, printToUserTimezone } from '@common/utils/dateUtils';
const focusOnError = createDecorator(); // must be outside the component
const GiftCardPurchaseModal = ({ isOpen = false, onRequestClose, type, product, onSubmit, onMoveToBag }) => {
    var _a, _b;
    // States
    const [errors, setErrors] = useState([]); // Form errors
    const [amountErrors, setAmountErrors] = useState([]); // Form amount errors
    const [formdef, setFormdef] = useState(); // Form definition
    const [initialValues, setInitialValues] = useState(); // Form initial values
    const [selectedAmount, setSelectedAmount] = useState(''); // Used to highlight the selected amount button
    const [customVisible, setCustomVisible] = useState(false); // Determine if `amount` form field must be visible (to allow a custom amount)
    const [messageVisible, setMessageVisible] = useState(false); // Determine if `receiverName` and `yourMessage` form fields must be visible
    const [messageLength, setMessageLength] = useState(200); // Remaining characters for the `yourMessage` field
    const [dateVisible, setDateVisible] = useState(false); // Determine if `delayedDate` and `delayedTime` form fields must be visible
    const [maxDelayedDate, setMaxDelayedDate] = useState(new Date().toISOString()); // Max date for `delayedDate` form field
    const [selectedDate, setSelectedDate] = useState();
    // Hooks
    const { t, i18n } = useTranslation();
    // Ref
    const amountRef = useRef();
    // Utils
    const intlNumberFormater = new Intl.NumberFormat(((_a = window.locale) === null || _a === void 0 ? void 0 : _a.replace('_', '-')) || `${window.language}-${window.country}` || i18n.language);
    const regexRemoveMask = /[^0-9]/g;
    // Time zone calculation
    const timezones = (_b = window.timezones) === null || _b === void 0 ? void 0 : _b.split('|');
    const userTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    let actualTimezone = timezones[0];
    if (timezones.includes(userTz)) {
        actualTimezone = userTz;
    }
    const tzDate = new Date();
    tzDate.setMinutes(tzDate.getMinutes() + getTimezoneOffset(actualTimezone));
    const deliveryTimeRangeOptions = useMemo(() => {
        // Delivery time range options for the delayed time field
        const deliveryTimeRange = 30;
        const options = Array.from({ length: 24 * 60 / deliveryTimeRange }, (_, i) => {
            var _a, _b;
            let baseTime = new Date('2021-01-01T00:00:00');
            baseTime.setMinutes(baseTime.getMinutes() + i * deliveryTimeRange);
            const value = baseTime.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
            const startTime = (_a = baseTime.toLocaleTimeString(window.country === 'US' || window.country === 'CA' ? 'en-US' : window.country, { hour: '2-digit', minute: '2-digit' })) === null || _a === void 0 ? void 0 : _a.toLocaleLowerCase();
            baseTime.setMinutes(baseTime.getMinutes() + deliveryTimeRange);
            const endTime = (_b = baseTime.toLocaleTimeString(window.country === 'US' || window.country === 'CA' ? 'en-US' : window.country, { hour: '2-digit', minute: '2-digit' })) === null || _b === void 0 ? void 0 : _b.toLocaleLowerCase();
            if (selectedDate) {
                const now = tzDate;
                const selectedDateFormatted = new Date(selectedDate + 'T00:00:00.000');
                let timeOffset = getTimezoneOffset(actualTimezone);
                if (timeOffset < 0) {
                    timeOffset = -timeOffset;
                }
                selectedDateFormatted.setMinutes(selectedDateFormatted.getMinutes() + timeOffset);
                // disable past time options if today is selected
                if (selectedDateFormatted.toDateString() === now.toDateString() && baseTime.getMinutes() + (i * deliveryTimeRange) - 30 <= now.getHours() * 60 + now.getMinutes()) {
                    return { value, label: `${startTime} - ${endTime}`, disabled: true };
                }
            }
            return { value, label: `${startTime} - ${endTime}` };
        });
        options.unshift({ value: '', label: t('gc.choosetime'), disabled: true });
        return options;
    }, [selectedDate]);
    // Boot
    useEffect(() => {
        if (isOpen) {
            let val = {};
            api.getForm('giftCardConfiguration', val, true).then(data => {
                if (type == 'create') {
                    setInitialValues(val);
                    setFormdef(data);
                }
                else if (type == 'edit') {
                    // Custom amount options
                    let value = product.singleItemValue ? product.singleItemValue.toString() : product.price ? product.price.sales.value.toString() : '';
                    const amount = product.gcConfigurations.find(am => am.number == value);
                    if (!!amount) {
                        setSelectedAmount(amount.number);
                    }
                    else {
                        // Custom amount visibility
                        value = intlNumberFormater.format(value);
                        setCustomVisible(true);
                        setSelectedAmount('custom');
                    }
                    // Custom message options
                    if (product.giftCardAttributes && !!product.giftCardAttributes.message) {
                        setMessageVisible(true);
                        setMessageLength(200 - product.giftCardAttributes.message.length);
                    }
                    // Delayed date options
                    const isDelayed = product.giftCardAttributes && !!product.giftCardAttributes.sendDate;
                    data.deliveryDate.selectedOption = isDelayed ? 'later' : 'today';
                    data.deliveryDate.htmlValue = isDelayed ? 'later' : 'today';
                    data.deliveryDate.options = data.deliveryDate.options.map(option => {
                        if (option.value == 'today') {
                            if (isDelayed) {
                                delete option.checked;
                                delete option.selected;
                            }
                            else {
                                option.checked = true;
                                option.selected = true;
                            }
                        }
                        else if (option.value == 'later') {
                            if (!isDelayed) {
                                delete option.checked;
                                delete option.selected;
                            }
                            else {
                                option.checked = true;
                                option.selected = true;
                            }
                        }
                        return option;
                    });
                    setFormdef(data);
                    changeDelayDate(isDelayed ? 'later' : 'today');
                    if (product.giftCardAttributes) {
                        const currentDate = (product.giftCardAttributes.sendDate && printToUserTimezone(product.giftCardAttributes.sendDate, 'date', 'short', true)) || new Date(tzDate.getTime() + 60 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
                        const currentTime = (product.giftCardAttributes.sendDate && printToUserTimezone(product.giftCardAttributes.sendDate, 'time', 'short', true));
                        setInitialValues({
                            amount: value,
                            delayedDate: currentDate,
                            delayedTime: currentTime,
                            deliveryDate: isDelayed ? 'later' : 'today',
                            includeMessage: !!product.giftCardAttributes.message,
                            receiverEmail: product.giftCardAttributes.recipientEmail,
                            receiverEmailVerification: product.giftCardAttributes.recipientEmail,
                            receiverName: product.giftCardAttributes.recipientName,
                            yourMessage: product.giftCardAttributes.message
                        });
                        setSelectedDate(currentDate);
                    }
                    else {
                        //giftcard in wishlist not configured
                        setInitialValues(val);
                    }
                }
            });
        }
    }, [isOpen]);
    /**
     * Validate the form
     */
    const validate = async (values) => {
        var _a;
        values.amount = (_a = values.amount) === null || _a === void 0 ? void 0 : _a.replace(regexRemoveMask, ''); // Remove amount mask before validation
        let errors = await formutils.validateForm(values, formdef, 'giftCardConfiguration');
        // Range error
        if (parseFloat(values.amount) > product.gcLimits.maxValue.value || parseFloat(values.amount) < product.gcLimits.minValue.value) {
            errors.amount = t('gc.invalid.amount', `Please enter an amount between ${product.gcLimits.minValue.formattedValue} and ${product.gcLimits.maxValue.formattedValue}`, { 0: product.gcLimits.minValue.formattedValue, 1: product.gcLimits.maxValue.formattedValue });
        }
        // Decimal error
        if (!Number.isInteger(parseFloat(values.amount))) {
            errors.amount = t('gc.decimal.amount');
        }
        // Empty amount
        if (!!!values.amount) {
            errors.amount = t('label.requiredfield');
        }
        // Message errors
        if (values.includeMessage) {
            if (!values.receiverName || values.receiverName == '') {
                errors.receiverName = t('label.requiredfield');
            }
            if (!values.yourMessage || values.yourMessage == '') {
                errors.yourMessage = t('label.requiredfield');
            }
        }
        // Delayed date errors
        let delayedErrors;
        if (values.deliveryDate == 'later') {
            if (!values.delayedDate) {
                errors.delayedDate = t('label.requiredfield');
                delayedErrors = [t('gc.delayed.error')];
            }
            if (!values.delayedTime) {
                errors.delayedTime = t('label.requiredfield');
                delayedErrors = [t('gc.delayed.error')];
            }
            const now = tzDate;
            const selectedDateFormatted = new Date(`${values.delayedDate}T${values.delayedTime}`);
            if (selectedDateFormatted <= now) {
                errors.delayedTime = t('gc.delayed.error');
                delayedErrors = [t('gc.delayed.error')];
            }
        }
        return errors;
    };
    /**
     * Add the product to cart
     */
    const addToBag = async (values) => {
        //addToBagFromWishlist
        const isAddToBag = values.action == 'addToBag';
        setErrors([]);
        let errors = await validate(values);
        if (errors && Object.keys(errors).length > 0) {
            return errors;
        }
        let actualDate = tzDate.toISOString();
        if (values.deliveryDate == 'today') {
            actualDate = null;
        }
        else if (values.deliveryDate == 'later') {
            actualDate = values.delayedDate + 'T' + values.delayedTime + ':00.000';
            let actualDateConversion = new Date(actualDate);
            actualDateConversion.setMinutes(actualDateConversion.getMinutes() - getTimezoneOffset(actualTimezone));
            actualDate = actualDateConversion.toISOString();
        }
        let giftCard = {
            gcAmount: values.amount,
            gcEmail: values.receiverEmail,
            gcName: values.includeMessage ? values.receiverName : null,
            gcMessage: values.includeMessage ? values.yourMessage : null,
            gcDate: actualDate
        };
        if (type == 'create') {
            onSubmit(giftCard);
        }
        else if (type == 'edit') {
            if (isAddToBag) {
                onMoveToBag(Object.assign({ gcUUID: product.UUID, gcPID: product.id }, giftCard), formdef);
            }
            else {
                onSubmit(Object.assign({ gcUUID: product.UUID, gcPID: product.id }, giftCard), formdef);
            }
        }
        closeModal();
    };
    /**
     * Change option values for the delayed date inputs
     */
    const changeDelayDate = (option) => {
        if (option == 'later') {
            let delay = tzDate;
            delay.setDate(delay.getDate() + product.gcMaxDelayDate);
            setMaxDelayedDate(() => delay.toISOString());
            setDateVisible(() => true);
        }
        else if (option == 'today') {
            setDateVisible(() => false);
        }
    };
    /**
     * Close the modal, then reset all fields
     */
    const closeModal = () => {
        onRequestClose();
        setFormdef(null);
        setInitialValues(null);
        setSelectedAmount('');
        setCustomVisible(false);
        setMessageVisible(false);
        setMessageLength(200);
        setDateVisible(false);
    };
    if (!formdef) {
        return React.createElement(React.Fragment, null);
    }
    return (React.createElement(ModalDialog, { className: 'modal--fixed gc-config-modal', overlayClassName: "overlay-visible", isOpen: isOpen, onRequestClose: () => closeModal(), noPadding: true, hideHeader: true },
        React.createElement("div", { className: "product-notify" },
            React.createElement("div", { className: "product-notify__main", style: { backgroundImage: "url('" + product.imgs.urls[0] + "')" } }),
            React.createElement("div", { className: "product-notify__detail giftcard-form" },
                React.createElement("h2", null, t('gc.digitalgiftcard')),
                React.createElement(Form, { onSubmit: addToBag, initialValues: initialValues, validate: validate, decorators: [focusOnError], render: ({ handleSubmit, form, submitting, pristine, values }) => {
                        var _a;
                        return (React.createElement("form", { onSubmit: handleSubmit },
                            React.createElement(React.Fragment, null,
                                React.createElement("p", { className: 'gc-amount' }, t('gc.selectamount').toUpperCase()),
                                React.createElement("div", { className: "d-flex gc-amount-btn-container", ref: amountRef },
                                    product.gcConfigurations.map(card => (React.createElement(Button, { key: card.number, onClick: () => {
                                            setCustomVisible(false);
                                            setSelectedAmount(card.number);
                                            setAmountErrors([]);
                                            form.batch(() => form.change('amount', card.number));
                                        }, type: 'button', cls: ['button--tertiary'], className: selectedAmount == card.number ? 'gc-amount-btn-alt' : 'gc-amount-btn' }, card.formatted))),
                                    React.createElement(Button, { onClick: () => {
                                            setCustomVisible(true);
                                            setSelectedAmount('custom');
                                            setAmountErrors([]);
                                            form.batch(() => form.change('amount', ''));
                                        }, type: 'button', cls: ['button--tertiary'], className: selectedAmount == 'custom' ? 'gc-amount-btn-alt' : 'gc-amount-btn' }, t('gc.custom'))),
                                React.createElement("div", { className: "pw-error col-6" },
                                    React.createElement(ErrorBox, { errors: amountErrors, className: 'gc-error-box' })),
                                React.createElement("div", { className: classNames(["row", { 'd-none': !customVisible }, 'mt-12']) },
                                    React.createElement("div", { className: "col-6" },
                                        React.createElement(DwFormField, { def: formdef, name: 'amount', type: 'text', autocomplete: 'off', altLabel: t('gc.insertamount'), requiredLabel: true, maxLength: ((_a = intlNumberFormater.format(product.gcLimits.maxValue.value)) === null || _a === void 0 ? void 0 : _a.length) || 7, textPrefix: product.gcCurrency, onChange: (e) => {
                                                var _a;
                                                // remove mask used on amount
                                                const value = (_a = e.target.value) === null || _a === void 0 ? void 0 : _a.replace(regexRemoveMask, '');
                                                // avoid non-numeric values
                                                const isValidNumber = !Number.isNaN(Number(value));
                                                form.batch(() => {
                                                    form.change('amount', value && isValidNumber ? intlNumberFormater.format(isValidNumber ? value : value.slice(0, -1)) : '');
                                                });
                                            } })),
                                    React.createElement("div", { className: "col-2" },
                                        React.createElement(Tooltip, { ariaLabel: t('gc.amount.tooltip', `Insert an amount between ${product.gcLimits.minValue.formattedValue} and ${product.gcLimits.maxValue.formattedValue}`, { 0: product.gcLimits.minValue.formattedValue, 1: product.gcLimits.maxValue.formattedValue }), id: 'gc-email-tooltip', icon: "info", text: t('gc.amount.tooltip', `Insert an amount between <span class="nowrap">${product.gcLimits.minValue.formattedValue}</span> and <span class="nowrap">${product.gcLimits.maxValue.formattedValue}</span>`, { 0: product.gcLimits.minValue.formattedValue, 1: product.gcLimits.maxValue.formattedValue }), className: "ml-12" }))),
                                React.createElement("div", { className: "modal__separator" }),
                                React.createElement("div", { className: "row" },
                                    React.createElement("div", { className: "col-10" },
                                        React.createElement(DwFormField, { def: formdef, name: 'receiverEmail', type: 'text', autocomplete: 'off', altLabel: t('gc.receiver.email'), requiredLabel: true, preventPaste: true })),
                                    React.createElement("div", { className: "col-2" },
                                        React.createElement(Tooltip, { ariaLabel: t('gc.email.tooltip'), id: 'gc-email-tooltip', icon: "info", text: t('gc.email.tooltip'), className: "ml-12" }))),
                                React.createElement("div", { className: "row" },
                                    React.createElement("div", { className: "col-12 col-md-10" },
                                        React.createElement(DwFormField, { def: formdef, name: 'receiverEmailVerification', type: 'text', autocomplete: 'off', altLabel: t('gc.receiver.emailverify'), requiredLabel: true, preventPaste: true }))),
                                React.createElement("div", { className: "row gc-custom-check" },
                                    React.createElement("div", { className: "col-10" },
                                        React.createElement(DwFormField, { def: formdef, name: 'includeMessage', type: 'checkbox', autocomplete: 'off', altLabel: t('gc.customMessage'), onChange: e => setMessageVisible(e.target.checked) }))),
                                messageVisible
                                    &&
                                        React.createElement(React.Fragment, null,
                                            React.createElement("div", { className: "row mt-16" },
                                                React.createElement("div", { className: "col-12" },
                                                    React.createElement(DwFormField, { def: formdef, name: 'receiverName', type: 'text', autocomplete: 'off', altLabel: t('gc.receiver.name'), requiredLabel: true }))),
                                            React.createElement("div", { className: "row" },
                                                React.createElement("div", { className: "col-12" },
                                                    React.createElement(DwFormField, { def: formdef, name: 'yourMessage', type: 'textarea', autocomplete: 'off', altLabel: t('gc.yourmessage'), maxLength: 200, onChange: e => setMessageLength(200 - e.target.value.length), groupClassName: 'mb-0', requiredLabel: true }),
                                                    React.createElement("span", { className: "field-sublabel" }, messageLength + ' ' + t('gc.charactersleft'))))),
                                React.createElement("div", { className: "modal__separator" }),
                                React.createElement("div", { className: "row" },
                                    React.createElement("div", { className: "col-12" },
                                        React.createElement("span", { className: "field-label d-block" },
                                            t('gc.prefdeliverydate'),
                                            "*"),
                                        React.createElement(DwFormField, { def: formdef, name: 'deliveryDate', type: 'optionbox', autocomplete: 'off', hiddenLabel: true, fieldType: 'radio', onChange: e => {
                                                const option = e.target.value;
                                                changeDelayDate(option);
                                            } }))),
                                dateVisible
                                    &&
                                        React.createElement("div", { className: "row mt-8" },
                                            React.createElement("div", { className: "col-6" },
                                                React.createElement(DwFormField, { def: formdef, name: 'delayedDate', type: 'date', autocomplete: 'off', altLabel: t('gc.choosedate'), requiredLabel: true, onChange: e => { var _a; return setSelectedDate((_a = e.target) === null || _a === void 0 ? void 0 : _a.value); }, minDate: tzDate.getFullYear() + '-' + ("0" + (tzDate.getMonth() + 1)).slice(-2) + '-' + ("0" + tzDate.getDate()).slice(-2), maxDate: maxDelayedDate.split('T')[0] })),
                                            React.createElement("div", { className: "col-6" },
                                                React.createElement(DwFormField, { def: formdef, name: 'delayedTime', type: "select", altLabel: t('gc.choosetime'), requiredLabel: true, options: deliveryTimeRangeOptions }))),
                                React.createElement("div", { className: "pw-error col-6" },
                                    React.createElement(ErrorBox, { errors: errors, className: 'gc-error-box' })),
                                onMoveToBag &&
                                    React.createElement("div", { className: "row button-row" },
                                        React.createElement("div", { className: "col-12" },
                                            React.createElement(Button, { type: "submit", disabled: submitting, className: 'button--primary', onClick: () => form.change('action', 'addToBag') }, t('button.gcaddtobag')))),
                                React.createElement("div", { className: classNames("row button-row", onMoveToBag ? "giftcard-update-button-row" : "") },
                                    React.createElement("div", { className: "col-12" },
                                        React.createElement(Button, { onClick: () => {
                                                var _a;
                                                if (type !== 'create' && !submitting) {
                                                    analytics.editTheGiftcard();
                                                }
                                                if (!customVisible && (!values.amount || values.amount == '')) {
                                                    setAmountErrors([t('gc.amount.error')]);
                                                    (_a = amountRef.current) === null || _a === void 0 ? void 0 : _a.scrollIntoView({ block: 'center' });
                                                }
                                            }, type: "submit", disabled: submitting, cls: [type == 'create' ? 'button--primary' : 'button--secondary'] }, t(type == 'create' ? 'button.gcaddtobag' : 'button.update')))),
                                React.createElement("div", { className: "row cancel-button" },
                                    React.createElement("div", { className: "col-12" },
                                        React.createElement(Button, { type: 'button', disabled: submitting, cls: ['button--arrow-left'], onClick: () => closeModal() }, t('button.gccancel').toUpperCase()))))));
                    } })))));
};
export default GiftCardPurchaseModal;
